<template>
  <main id="counter_from_start" class="gameplay">
    <GameHeader
      :round-step-list="roundStepList"
      :current-round-index="currentRoundIndex"
      :correct-points="currentRound.question.correctPoints"
    >
      <template v-slot:timer>
        <GameTimer ref="game-timer-ref" @time:expired="emitGameOver" />
      </template>
    </GameHeader>

    <section class="questions">
      <BaseImage
        :src="currentRound.question.imgSrc"
        image-class="quest_img"
        alt="question"
      />

      <p class="quest_title">
        {{ currentRound.question.text }}
      </p>

      <div class="question_btns">
        <AnswerItem
          v-for="answer in currentRound.answerList"
          :key="`${currentRoundId}_${answer.text}`"
          :text="answer.text"
          :answered="currentRound.question.answered"
          :correct="currentRound.question.correct"
          :selected="answer.text === currentRound.question.__answerExtraInfo?.text"
          :user-chosen="currentRound.question.__answerExtraInfo?.jokerUsedType !== gameJokerTypeEnum.CHEAT"
          :disabled="isUserInteractionDisabled"
          @select:answer="handleAnswerSelect"
        />
      </div>
    </section>
  </main>

  <GameFooter>
    <template v-slot>
      <JokerItem
        v-for="joker in jokerList"
        :key="joker.type"
        :type="joker.type"
        :total="joker.total"
        :disabled="joker.used || isUserInteractionDisabled"
        @select:joker="handleSelectJoker"
      />
    </template>
  </GameFooter>
</template>

<script setup>
import { computed, onMounted, reactive, ref, useTemplateRef } from 'vue';
import { Howl } from 'howler';
import BaseImage from '@/components/image/BaseImage.vue';
import GameTimer from '@/components/timer/GameTimer.vue';
import GameHeader from '@/views/game/components/GameHeader.vue';
import GameFooter from '@/views/game/components/GameFooter.vue';
import AnswerItem from '@/views/game/components/item/AnswerItem.vue';
import JokerItem from '@/views/game/components/item/JokerItem.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useAuthStore from '@/store/modules/auth.js';
import useGameStore from '@/store/modules/game.js';
import { apiUseGameJoker, apiSendGameAnswer } from '@/services/game.js';
import { gameJokerTypeEnum } from '@/constants/game.js';

const QUESTION_RESULT_DELAY = 1500;

const emits = defineEmits(['game:over']);

const gameTimerRef = useTemplateRef('game-timer-ref');

const appSettingsStore = useAppSettingsStore();
const authStore = useAuthStore();
const gameStore = useGameStore();

const jokerList = ref([]);
const currentRoundIndex = ref(0);
const validatingAnswer = ref(false);
const applyingJoker = ref(false);
const sounds = reactive({
  correctAnswer: null,
  wrongAnswer: null,
  joker: null,
});

const roundStepList = computed(() => gameStore.gameRoundList.map(({ id, answered, correct }, index) => ({
  gameRoundId: id,
  answered,
  correct,
  index,
})));

const currentRound = computed(() => {
  const data = gameStore.gameRoundList[currentRoundIndex.value];

  if (!data) { return { question: {}, answerList: [] }; }

  const constructQuestion = ({ id, question, img, answered, correct, correctPoints, __answerExtraInfo }) =>
    ({ gameRoundId: id, text: question, imgSrc: img, answered, correct, correctPoints, __answerExtraInfo });

  const constructAnswerList = ({ answers }) => {
    if (!Array.isArray(answers)) { return []; }

    return answers.map((answer) => ({ text: answer }));
  };

  return {
    question: constructQuestion(data),
    answerList: constructAnswerList(data),
  };
});

const currentRoundId = computed(() => currentRound.value.question.gameRoundId);

const isUserInteractionDisabled = computed(() =>
  validatingAnswer.value ||
  applyingJoker.value ||
  currentRound.value.question.answered,
);

const emitGameOver = ({ completed = false } = {}) => {
  emits('game:over', { completed });
};

const checkAndStopTimer = () => {
  if (currentRoundIndex.value === (gameStore.gameRoundList.length - 1)) {
    gameTimerRef.value?.stopTimer?.();
  }
};

const playJokerSound = () => {
  if (appSettingsStore.isSoundEnabled) {
    sounds.joker.play();
  }
};

const handleAnswerSelect = async ({ text, jokerUsedType = null }) => {
  validatingAnswer.value = true;

  checkAndStopTimer();

  gameStore.updateGameRoundItem({
    roundIndex: currentRoundIndex.value,
    data: {
      __answerExtraInfo: { text, jokerUsedType },
    },
  });

  try {
    const queryParams = {
      gameId: gameStore.gameId,
      miniGameId: currentRound.value.question.gameRoundId,
      answer: text,
    };

    const { isCorrect } = await apiSendGameAnswer({ queryParams });

    gameStore.updateGameRoundItem({
      roundIndex: currentRoundIndex.value,
      data: {
        answered: true,
        correct: isCorrect,
      },
    });

    if (appSettingsStore.isSoundEnabled) {
      if (isCorrect) {
        sounds.correctAnswer.play();
      } else {
        sounds.wrongAnswer.play();
      }
    }
  } catch {
    // Intentionally ignoring errors - we want to continue the game flow even if answer submission fails
  }

  validatingAnswer.value = false;

  setTimeout(() => {
    if (currentRoundIndex.value < (gameStore.gameRoundList.length - 1)) {
      ++currentRoundIndex.value;
    } else {
      emitGameOver({ completed: true });
    }
  }, QUESTION_RESULT_DELAY);
};

const handleSelectJoker = async ({ type }) => {
  try {
    applyingJoker.value = true;

    if (type === gameJokerTypeEnum.CHEAT) {
      checkAndStopTimer();
    }

    const queryParams = {
      msisdn: authStore.user.msisdn,
      gameId: gameStore.gameId,
      miniGameId: currentRoundId.value,
      possibleAnswers: currentRound.value.answerList.map(({ text }) => text),
      helpType: type,
    };

    const { parsedData } = await apiUseGameJoker({ queryParams });

    if (type === gameJokerTypeEnum.FIFTY_FIFTY) {
      playJokerSound();

      gameStore.updateGameRoundItem({
        roundIndex: currentRoundIndex.value,
        data: { answers: parsedData },
      });
    } else if (type === gameJokerTypeEnum.CHEAT) {
      await handleAnswerSelect({ text: parsedData[0], jokerUsedType: gameJokerTypeEnum.CHEAT });
    }

    const findJokerIndex = jokerList.value.findIndex((joker) => joker.type === type);

    if (findJokerIndex > -1) {
      jokerList.value[findJokerIndex].total -= 1;
      jokerList.value[findJokerIndex].used = true;
    }

    applyingJoker.value = false;
  } catch {
    applyingJoker.value = false;
  }
};

const initializeJokers = () => {
  jokerList.value = [
    {
      type: gameJokerTypeEnum.FIFTY_FIFTY,
      total: authStore.user.jokersFifty,
    },
    {
      type: gameJokerTypeEnum.CHEAT,
      total: authStore.user.jokersCheat,
    },
  ].filter(({ total }) => total > 0)
    .map(info => ({ ...info, used: false }));
};

onMounted(() => {
  initializeJokers();

  if (appSettingsStore.isSoundEnabled) {
    sounds.correctAnswer = new Howl({ src: ['/static/sounds/right.mp3'] });
    sounds.wrongAnswer = new Howl({ src: ['/static/sounds/wrong.mp3'] });
    sounds.joker = new Howl({ src: ['/static/sounds/joker.mp3'] });
  }
});
</script>
